import { ConnectionType, KnovioLabel } from '../../../api/connections.dao';
import {
    DETAILS_PLACEHOLDER_TEXT_DEFAULT_VALUE,
    knovioPublishInfoMessage,
    passportReplayInfoMessage,
    webinarAutoRegistrationInfoMessage,
    webinarSyncMeetingAttendeeInfoMessage,
    zoomMeetingAutoRegistrationInfoMessage,
    zoomMeetingAutoSyncInfoMessage
} from '../../../constants/connections-labels.constants';
import { trimSpaces, trimSpacesLeft } from '../../formatters';
import { email, liveStreamUrl, passCode, plainText, richText, url } from '../../validators';

/* CONNECTION DTO KEYS */
export enum ConnectionFields {
    MEETING_CONNECTION_ID = 'meetingConnectionId',
    ALTERNATIVE_LINK = 'alternativeLink',
    HOST_LINK = 'hostLink',
    CONNECTION_ACCOUNT = 'connectionAccount',
    CONNECTION_HOST_KEY = 'connectionHostKey',
    CONNECTION_ID = 'connectionId',
    CONNECTION_LINK = 'connectionLink',
    CONNECTION_NOTES = 'connectionNotes',
    CONNECTION_PASSWORD = 'connectionPassword',
    DIAL_FOR_VS = 'dialForVs',
    MEETING_INVITATION = 'meetingInvitation',
    SIP = 'sip',
    TYPE = 'type',
    KNOVIO_ACCOUNT_NAME = 'accountName',
    KNOVIO_NAME = 'knovioName',
    EMBED_CODE_ID = 'embedCodeId',
    LIVE_STREAM_ENABLED = 'liveStreamEnabled',
    PRIMARY_LIVE_STREAM_URL = 'primaryLiveStreamUrl',
    PRIMARY_LIVE_STREAM_NAME = 'primaryLiveStreamName',
    BACKUP_LIVE_STREAM_URL = 'backupLiveStreamUrl',
    BACKUP_LIVE_STREAM_NAME = 'backupLiveStreamName',
    NAME = 'name',
    LABEL = 'label',
    CONNECTION_TOPIC = 'connectionTopic',
    ZOOM_DESCRIPTION = 'zoomDescription',
    MEETING_LOCATION = 'meetingLocation',
    SHOWCASE_PAGE_PATH = 'showcasePagePath',
    SHOWCASE_NAME = 'showcaseName',
    ATTENDEE_EMAIL = 'attendeeEmail',
    AUTO_REGISTRATION_ENABLED = 'autoRegistrationEnabled',
    SYNC_MEETING_ATTENDEE_WITH_ZOOM_ENABLED = 'autoSyncEnabled',
    REGISTRATION_ENABLED = 'registrationEnabled',
    AUTOMATICALLY_APPROVAL = 'approvalType',
    REGISTRATION_CONFIRMATION_EMAIL = 'registrantsConfirmationEmail',
    VERACAST_EVENT_ID = 'veracastEventID',
    VERACAST_CONF_ID = 'veracastConfID',
    CLIENT_APP_LINK = 'clientAppLink',
    PASSPORT_REPLAY = 'passportReplayEnabled',
    PUBLISH_CONNECTION = 'publishEnabled',
    DETAILS_PLACEHOLDER_TEXT = 'detailsPlaceholderText',
    PODIUM_LINK = 'podiumLink',
    PODIUM_ACCESS_CODE ='podiumAccessCode',
    PODIUM_EMBED_CODE ='podiumEmbedCode',
    REGISTRATION_LINK = 'registrationUrl',
    AUTOMATICALLY_RECORD = 'autoRecording',
    MEETING_UNIQUE_REFERENCE = 'meetingUniqueReference',
}

export default {
    /* WEBEX, BLUEJEANS, WEBINAR, ZOOM, TEAMS */
    [ConnectionFields.CONNECTION_LINK]: {
        label: 'Connection Link',
        validators: [ plainText.maxLength.medium, url.regExp ],
        formatters: [ trimSpaces ],
    },
    [ConnectionFields.REGISTRATION_LINK]: {
        label: 'Registration Link',
        validators: [ plainText.maxLength.medium, url.regExp ],
        formatters: [ trimSpaces ],
    },
    [ConnectionFields.LABEL]: {
        label: 'Label',
        validators: [ plainText.maxLength.short ],
    },
    [ConnectionFields.ALTERNATIVE_LINK]: {
        label: 'Alternative Link',
        validators: [ plainText.maxLength.medium, url.regExp ],
        formatters: [ trimSpaces ],
    },
    [ConnectionFields.HOST_LINK]: {
        label: 'Host Link',
        validators: [ plainText.maxLength.medium, url.regExp ],
        formatters: [ trimSpaces ],
    },
    [ConnectionFields.CONNECTION_ID]: {
        label: 'Connection ID',
        validators: [ plainText.required, plainText.maxLength.short ],
        formatters: [ trimSpacesLeft ],
    },
    [ConnectionFields.CONNECTION_PASSWORD]: {
        label: 'Connection Password',
        validators: [ plainText.maxLength.short ],
        formatters: [ trimSpaces ],
    },
    [ConnectionFields.CONNECTION_ACCOUNT]: {
        label: 'Connection Account',
        validators: [ plainText.maxLength.short ],
        formatters: [ trimSpacesLeft ],
    },
    [ConnectionFields.SIP]: {
        label: 'SIP',
        validators: [ plainText.maxLength.short ],
        formatters: [ trimSpacesLeft ],
    },
    [ConnectionFields.CONNECTION_HOST_KEY]: {
        label: 'Connection Host Key',
        validators: [ plainText.maxLength.short ],
        formatters: [ trimSpacesLeft ],
    },
    [ConnectionFields.DIAL_FOR_VS]: {
        label: 'Dial for VS',
        valueOptions: [ null, 'In', 'Out' ],
    },
    [ConnectionFields.CONNECTION_TOPIC]: {
        label: 'Connection Topic',
        validators: [ plainText.maxLength.short ],
        formatters: [ trimSpacesLeft ],
    },
    [ConnectionFields.MEETING_INVITATION]: {
        label: 'Meeting Invitation',
        validators: [ plainText.maxLength.long ],
        formatters: [ trimSpacesLeft ],
    },
    [ConnectionFields.CONNECTION_NOTES]: {
        label: 'Connection Notes',
        validators: [ plainText.maxLength.long ],
        formatters: [ trimSpacesLeft ],
    },
    [ConnectionFields.MEETING_UNIQUE_REFERENCE]: {
        label: 'Unique Reference',
        valueOptions: [ { label: 'No Unique Reference', value: 0 }, 1, 2, 3, 4, 5 ],
    },

    /* WEBINAR */
    [ConnectionFields.ZOOM_DESCRIPTION]: {
        label: 'Zoom Description',
        validators: [ plainText.maxLength.long ],
        formatters: [ trimSpacesLeft ],
    },
    [ConnectionFields.AUTOMATICALLY_RECORD]: {
        defaultValue: true,
        toggleValues: {
            enabled: {
                value: 'Automatically Record enabled',
                id: 'oe.connections.automaticallyRecordEnabled',
            },
            disabled: {
                value: 'Automatically Record disabled',
                id: 'oe.connections.automaticallyRecordDisabled',
            }
        },
    },
    /* KNOVIO */
    [ConnectionFields.PASSPORT_REPLAY]: {
        defaultValue: false,
        toggleInfoMessage: passportReplayInfoMessage,
        toggleValues: {
            enabled: {
                value: 'Passport Replay is enabled',
                id: 'oe.connections.passportReplayEnabled',
            },
            disabled: {
                value: 'Passport Replay is not enabled',
                id: 'oe.connections.passportReplayDisabled',
            }
        },
        formatters: [ Boolean ],
    },
    [ConnectionFields.PUBLISH_CONNECTION]: {
        defaultValue: true,
        toggleInfoMessage: knovioPublishInfoMessage,
        toggleValues: {
            enabled: {
                value: 'Connection is Published',
                id: 'oe.connections.publishEnabled',
            },
            disabled: {
                value: 'Connection is Unpublished',
                id: 'oe.connections.publishDisabled',
            }
        },
        formatters: [ Boolean ],
    },
    [ConnectionFields.DETAILS_PLACEHOLDER_TEXT]: {
        defaultValue: DETAILS_PLACEHOLDER_TEXT_DEFAULT_VALUE,
        label: 'Placeholder Text',
        validators: [ richText.maxLength.short ],
        formatters: [ trimSpacesLeft ],
    },
    [ConnectionFields.KNOVIO_ACCOUNT_NAME]: {
        label: `${KnovioLabel.OEStream} Account Name`,
        validators: [ plainText.maxLength.short ],
        formatters: [ trimSpacesLeft ],
    },
    [ConnectionFields.KNOVIO_NAME]: {
        label: `${KnovioLabel.Project} Name`,
        validators: [ plainText.maxLength.short ],
        formatters: [ trimSpacesLeft ],
    },
    [ConnectionFields.EMBED_CODE_ID]: {
        label: 'Embed Code Id',
        validators: [ plainText.required, plainText.maxLength.short ],
        formatters: [ trimSpaces ],
    },
    [ConnectionFields.LIVE_STREAM_ENABLED]: {
        label: 'Live Stream',
        defaultValue: false,
        formatters: [ Boolean ],
    },
    [ConnectionFields.PRIMARY_LIVE_STREAM_URL]: {
        label: 'Primary RTMP Stream URL',
        validators: [ plainText.maxLength.medium, liveStreamUrl.rtmp ],
        formatters: [ trimSpaces ],
    },
    [ConnectionFields.PRIMARY_LIVE_STREAM_NAME]: {
        label: 'RTMP Stream Name',
        validators: [ plainText.maxLength.short ],
        formatters: [ trimSpacesLeft ],
    },
    [ConnectionFields.BACKUP_LIVE_STREAM_URL]: {
        label: 'Backup RTMP Stream URL',
        validators: [ plainText.maxLength.medium, liveStreamUrl.rtmp ],
        formatters: [ trimSpaces ],
    },
    [ConnectionFields.BACKUP_LIVE_STREAM_NAME]: {
        label: 'Backup RTMP Stream Name',
        validators: [ plainText.maxLength.short ],
        formatters: [ trimSpacesLeft ],
    },
    [ConnectionFields.PODIUM_LINK]: {
        label: 'Podium 1.0 Link',
        validators: [ url.regExp ],
        formatters: [ trimSpaces ]
    },
    [ConnectionFields.PODIUM_ACCESS_CODE]: {
        label: 'Podium 1.0 Access Code',
        validators: [ passCode.maxLength.short, passCode.minLength.short ],
        formatters: [ trimSpaces ]
    },
    [ConnectionFields.PODIUM_EMBED_CODE]: {
        label: 'One-Click Podium 1.0 Link',
        validators: [ url.regExp ],
        formatters: [ trimSpaces ]
    },
    /* OTHER */
    [ConnectionFields.NAME]: {
        label: 'Name',
        validators: [ plainText.required, plainText.maxLength.short ],
        formatters: [ trimSpacesLeft ],
    },
    /* IN-PERSON */
    [ConnectionFields.MEETING_LOCATION]: {
        label: 'Meeting Location',
        validators: [ plainText.required, richText.maxLength.long ],
        formatters: [ trimSpacesLeft ],
    },
    /* SHOWCASE PAGE PATH */
    [ConnectionFields.SHOWCASE_NAME]: {
        label: 'Showcase Name',
        validators: [ plainText.maxLength.short ],
        formatters: [ trimSpacesLeft ],
    },
    [ConnectionFields.SHOWCASE_PAGE_PATH]: {
        label: 'Showcase Page Path',
        validators: [ plainText.required, plainText.maxLength.short ],
        formatters: [ trimSpacesLeft ],
    },
    /* UNIQUE JOINING LINK */
    [ConnectionFields.ATTENDEE_EMAIL]: {
        label: 'Attendee Email',
        validators: [ plainText.maxLength.short, email.regExp ],
        formatters: [ trimSpaces ]
    },
    [ConnectionFields.AUTO_REGISTRATION_ENABLED]: {
        label: '',
        defaultValue: false,
        toggleInfoMessage: {
            [ConnectionType.Webinar]: webinarAutoRegistrationInfoMessage,
            [ConnectionType.Zoom]: zoomMeetingAutoRegistrationInfoMessage,
        },
        toggleValues: {
            enabled: {
                value: 'Passport Auto Registration enabled',
                id: 'oe.zoomWebinar.autoRegistrationEnabled',
            },
            disabled: {
                value: 'Passport Auto Registration disabled',
                id: 'oe.zoomWebinar.autoRegistrationDisabled',
            }
        },
        formatters: [ Boolean ],
    },
    [ConnectionFields.SYNC_MEETING_ATTENDEE_WITH_ZOOM_ENABLED]: {
        label: '',
        defaultValue: false,
        toggleInfoMessage: {
            [ConnectionType.Webinar]: webinarSyncMeetingAttendeeInfoMessage,
            [ConnectionType.Zoom]: zoomMeetingAutoSyncInfoMessage,
        },
        toggleValues: {
            enabled: {
                value: 'Sync Meeting Attendees with Zoom enabled',
                id: 'oe.zoomWebinar.syncMeetingAttendeesWithZoomEnabled',
            },
            disabled: {
                value: 'Sync Meeting Attendees with Zoom disabled',
                id: 'oe.zoomWebinar.syncMeetingAttendeesWithZoomDisabled',
            }
        },
        formatters: [ Boolean ],
    },
    [ConnectionFields.REGISTRATION_ENABLED]: {
        label: '',
        defaultValue: true,
        toggleValues: {
            enabled: {
                value: 'Registration enabled',
                id: 'oe.zoomWebinar.registrationEnabled',
            },
            disabled: {
                value: 'Registration disabled',
                id: 'oe.zoomWebinar.registrationDisabled',
            }
        },
        formatters: [ Boolean ],
    },
    [ConnectionFields.REGISTRATION_CONFIRMATION_EMAIL]: {
        label: '',
        defaultValue: true,
        toggleValues: {
            enabled: {
                value: 'Zoom registration emails enabled',
                id: 'oe.zoomWebinar.emailNotificationEnabled',
            },
            disabled: {
                value: 'Zoom registration emails disabled',
                id: 'oe.zoomWebinar.emailNotificationDisabled',
            }
        },
        formatters: [ Boolean ],
    },
    [ConnectionFields.AUTOMATICALLY_APPROVAL]: {
        label: '',
        defaultValue: true,
        toggleValues: {
            enabled: {
                value: 'Automatic approval',
                id: 'oe.zoomWebinar.automaticApproval',
            },
            disabled: {
                value: 'Manual approval',
                id: 'oe.zoomWebinar.manualApproval',
            }
        },
        formatters: [ Boolean ],
    },
    // Veracast
    [ConnectionFields.VERACAST_EVENT_ID]: {
        label: 'Veracast Event ID',
        validators: [plainText.required, plainText.maxLength.short],
        formatters: [trimSpacesLeft]
    },
    [ConnectionFields.VERACAST_CONF_ID]: {
        label: 'Veracast Conf ID',
        validators: [plainText.maxLength.short],
        formatters: [trimSpacesLeft]
    },
    [ConnectionFields.CLIENT_APP_LINK]: {
        label: 'Client App Link',
        validators: [plainText.maxLength.medium, url.regExp],
        formatters: [trimSpaces],
    }
};

export const fieldsWithoutCopyButton: ConnectionFields[] = [
    ConnectionFields.TYPE,
    ConnectionFields.DIAL_FOR_VS,
    ConnectionFields.LIVE_STREAM_ENABLED,
    ConnectionFields.MEETING_LOCATION,
    ConnectionFields.SYNC_MEETING_ATTENDEE_WITH_ZOOM_ENABLED,
    ConnectionFields.AUTO_REGISTRATION_ENABLED,
    ConnectionFields.REGISTRATION_ENABLED,
    ConnectionFields.AUTOMATICALLY_APPROVAL,
    ConnectionFields.REGISTRATION_CONFIRMATION_EMAIL,
    ConnectionFields.PASSPORT_REPLAY,
    ConnectionFields.PUBLISH_CONNECTION,
    ConnectionFields.DETAILS_PLACEHOLDER_TEXT,
    ConnectionFields.AUTOMATICALLY_RECORD
];

export const fieldsForLinkedKnovio: ConnectionFields[] = [
    ConnectionFields.PODIUM_LINK,
    ConnectionFields.PODIUM_ACCESS_CODE,
    ConnectionFields.PODIUM_EMBED_CODE,
];
